/**
 * GPU Engine Entry Point for Helios
 *
 * Detects the best available compute backend:
 *   1. WebGPU  (Chrome/Edge 113+, Firefox Nightly)
 *   2. WebGL2  (all modern browsers)
 *   3. CPU     (Web Workers, universal fallback)
 *
 * Exports a singleton GPUEngine that provides a unified interface
 * for force layout, similarity, heatmap, and clustering computations.
 */

import { WebGPUEngine } from './webgpu-engine';
import { WebGLFallback } from './webgl-fallback';
import { CPUFallback } from './cpu-fallback';
import type { PositionedNode, ClusterResult } from './webgpu-engine';

export type { PositionedNode, ClusterResult };

export type GPUBackend = 'webgpu' | 'webgl' | 'cpu';

export interface GPUEngineInterface {
  computeForceLayout(
    nodes: Array<{ id: string; x?: number; y?: number; mass?: number }>,
    edges: Array<{ source: number; target: number }>,
    iterations?: number,
    options?: {
      repulsion?: number;
      attraction?: number;
      damping?: number;
      gravity?: number;
    }
  ): Promise<PositionedNode[]>;

  computeEmbeddingSimilarity(
    vectorsA: Float32Array,
    vectorsB: Float32Array,
    dim: number
  ): Promise<Float32Array>;

  computeHeatmap(
    data: Float32Array,
    width: number,
    height: number
  ): Promise<Uint8ClampedArray>;

  computeClusterDetection(
    adjacencyMatrix: Float32Array,
    numClusters?: number
  ): Promise<ClusterResult[]>;

  getCapabilities(): unknown;
  dispose(): void;
}

class GPUEngineSingleton {
  private engine: GPUEngineInterface | null = null;
  private _backend: GPUBackend | null = null;
  private _isReady = false;
  private _initPromise: Promise<void> | null = null;

  get backend(): GPUBackend | null {
    return this._backend;
  }

  get isReady(): boolean {
    return this._isReady;
  }

  async init(): Promise<GPUBackend> {
    if (this._isReady && this._backend) {
      return this._backend;
    }

    if (this._initPromise) {
      await this._initPromise;
      return this._backend!;
    }

    this._initPromise = this._doInit();
    await this._initPromise;
    return this._backend!;
  }

  private async _doInit(): Promise<void> {
    // Try WebGPU first
    if (typeof navigator !== 'undefined' && 'gpu' in navigator) {
      try {
        const webgpu = new WebGPUEngine();
        await webgpu.init();
        this.engine = webgpu;
        this._backend = 'webgpu';
        this._isReady = true;
        console.log(
          '%c[Helios GPU] %cWebGPU backend active',
          'color: #00f0ff; font-weight: bold',
          'color: #22c55e'
        );
        return;
      } catch (err) {
        console.warn('[Helios GPU] WebGPU init failed, trying WebGL2:', err);
      }
    }

    // Try WebGL2 fallback
    try {
      const testCanvas =
        typeof OffscreenCanvas !== 'undefined'
          ? new OffscreenCanvas(1, 1)
          : document.createElement('canvas');
      const gl = testCanvas.getContext('webgl2');
      if (gl) {
        const ext = gl.getExtension('EXT_color_buffer_float');
        if (ext) {
          const webgl = new WebGLFallback();
          await webgl.init();
          this.engine = webgl;
          this._backend = 'webgl';
          this._isReady = true;
          console.log(
            '%c[Helios GPU] %cWebGL2 fallback active',
            'color: #00f0ff; font-weight: bold',
            'color: #eab308'
          );
          return;
        }
      }
    } catch (err) {
      console.warn('[Helios GPU] WebGL2 init failed, falling back to CPU:', err);
    }

    // CPU fallback
    const cpu = new CPUFallback();
    await cpu.init();
    this.engine = cpu;
    this._backend = 'cpu';
    this._isReady = true;
    console.log(
      '%c[Helios GPU] %cCPU fallback active (Web Workers)',
      'color: #00f0ff; font-weight: bold',
      'color: #ef4444'
    );
  }

  getEngine(): GPUEngineInterface {
    if (!this.engine) {
      throw new Error('GPU Engine not initialized. Call init() first.');
    }
    return this.engine;
  }

  async computeForceLayout(
    ...args: Parameters<GPUEngineInterface['computeForceLayout']>
  ): Promise<PositionedNode[]> {
    return this.getEngine().computeForceLayout(...args);
  }

  async computeEmbeddingSimilarity(
    ...args: Parameters<GPUEngineInterface['computeEmbeddingSimilarity']>
  ): Promise<Float32Array> {
    return this.getEngine().computeEmbeddingSimilarity(...args);
  }

  async computeHeatmap(
    ...args: Parameters<GPUEngineInterface['computeHeatmap']>
  ): Promise<Uint8ClampedArray> {
    return this.getEngine().computeHeatmap(...args);
  }

  async computeClusterDetection(
    ...args: Parameters<GPUEngineInterface['computeClusterDetection']>
  ): Promise<ClusterResult[]> {
    return this.getEngine().computeClusterDetection(...args);
  }

  getCapabilities(): unknown {
    return this.engine?.getCapabilities() ?? null;
  }

  dispose(): void {
    this.engine?.dispose();
    this.engine = null;
    this._backend = null;
    this._isReady = false;
    this._initPromise = null;
  }
}

/** Singleton GPU engine instance */
export const GPUEngine = new GPUEngineSingleton();
export default GPUEngine;
